import { Calendar, ClipboardPlus, Flag, Trash2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Code, CodeService } from "@/services/CodeService";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ObservationService } from "@/services/ObservationService";
import ObservationNewForm from "./ObservationNewForm";
import ObservationEditForm from "./ObservationEditForm";
import DeleteDialog from "../DeleteDialog";
import { AttachmentService } from "@/services/AttachmenteService";
import AttachmentImage from "../AttachmentImage";

interface ObservationDetails {
  id: string;
  name: string;
  typeCode: Code;
  modifiedDate: Date;
  attachmentUrl: string | null;
  isProcessing?: boolean;
  observedByName: string | null;
}

interface MapObservaationsProps {
  featureId: string;
}

const MapObservaations: React.FC<MapObservaationsProps> = ({ featureId }) => {
  const [observationDetails, setObservationDetails] = useState<ObservationDetails[]>([]);
  const [state, setState] = useState<"list" | "new" | "edit">("list");
  const [selectedObservationId, setSelectedObservationId] = useState<string | null>(null);
  const [showDeleteFeatureDialog, setShowDeleteFeatureDialog] = useState(false);
  const [observationCodes, setObservationCodes] = useState<Record<string, Code>>({});

  useEffect(() => {
    const initializeData = async () => {
      const [codes, observations] = await Promise.all([
        CodeService.getCodes2(),
        ObservationService.getByFeatureId(featureId),
      ]);

      const attachments = await AttachmentService.getOldestByEntityIds(observations.map(o => o.id));

      const record: Record<string, Code> = codes.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {} as Record<string, Code>);
      setObservationCodes(record);
      setObservationDetails(
        observations
          .map((observation) => ({
            id: observation.id!,
            name: observation.name,
            typeCode: record[observation.typeCodeId!] ?? {
              id: "",
              name: "N/A",
              type: "N/A",
            },
            modifiedDate: new Date(observation.observedAt ?? new Date()),
            attachmentUrl: attachments.find(a => a.entityId === observation.id)?.thumbnailUrl ?? null,
            isProcessing: attachments.find(a => a.entityId === observation.id)?.isProcessing ?? false,
            observedByName: observation.observedByName,
          }))
          .sort((a, b) => b.modifiedDate.getTime() - a.modifiedDate.getTime()),
      );
    };
    initializeData();
  }, [featureId]);

  const processingObservationIds = observationDetails
    .filter((item) => item.isProcessing)
    .map((item) => item.id)
    .sort()
    .join(",");

  useEffect(() => {
    if (state !== "list" || processingObservationIds.length === 0) {
      return;
    }

    const ids = processingObservationIds.split(",");
    let cancelled = false;
    let requestId = 0;

    const refreshProcessingObservations = async () => {
      const currentRequest = ++requestId;
      try {
        const attachments = await AttachmentService.getOldestByEntityIds(ids);
        if (cancelled || currentRequest !== requestId) {
          return;
        }

        const attachmentsByEntityId = new Map(
          attachments.map((attachment) => [attachment.entityId, attachment]),
        );
        setObservationDetails((current) => {
          let changed = false;
          const next = current.map((item) => {
            if (!ids.includes(item.id)) {
              return item;
            }

            const attachment = attachmentsByEntityId.get(item.id);
            if (!attachment) {
              return item;
            }

            const stillProcessing = attachment.isProcessing ?? false;
            const hasLocalPreview =
              !!item.attachmentUrl &&
              (item.attachmentUrl.startsWith("blob:") || item.attachmentUrl.startsWith("data:"));
            const attachmentUrl =
              stillProcessing && hasLocalPreview
                ? item.attachmentUrl
                : attachment.thumbnailUrl ?? null;

            if (item.attachmentUrl === attachmentUrl && item.isProcessing === stillProcessing) {
              return item;
            }

            changed = true;
            return { ...item, attachmentUrl, isProcessing: stillProcessing };
          });

          return changed ? next : current;
        });
      } catch {
        // Keep the current cards until the next poll.
      }
    };

    void refreshProcessingObservations();
    const interval = window.setInterval(refreshProcessingObservations, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [state, processingObservationIds]);

  return (
    <>
      {state === "list" && (
        <ClipboardPlus
          className="h-6 w-6 text-gray-500 group-hover:text-blue-600 transition-colors duration-200 cursor-pointer"
          onClick={() => setState(state === "list" ? "new" : "list")}
        />
      )}

      {state === "list" ? (
        observationDetails.map((item) => (
          <Card
            key={item.id}
            className="cursor-pointer transition-all hover:shadow-lg mb-2 mt-1"
            onClick={() => {
              setSelectedObservationId(item.id);
              setState("edit");
            }}
          >
            <CardHeader className="pb-1">
              <div className="flex items-start justify-between">
                <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                  {item.name}
                </CardTitle>
                <button
                  className="hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  onClick={async (e) => {
                    e.stopPropagation();
                    setSelectedObservationId(item.id);
                    setShowDeleteFeatureDialog(true);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <AttachmentImage
                  url={item.attachmentUrl}
                  isProcessing={item.isProcessing}
                  alt="Attachment"
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Flag className="h-4 w-4 text-gray-400" />
                        <span
                          className={`inline-flex items-center rounded-full text-xs font-medium`}
                        >
                          {item.typeCode?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                    <span>
                      <Calendar className="inline-block mr-1 h-3 w-3" />: {item.modifiedDate.toLocaleDateString()}
                    </span>
                    {item.observedByName && (
                      <Badge variant="secondary" className="text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {item.observedByName}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      ) : state === "new" ? (
        <ObservationNewForm
          featureId={featureId}
          showFooter={false}
          onClose={(observation, attachments) => {
            if (observation) {
              const createdObservation = {
                id: observation.id!,
                name: observation.name,
                typeCode: observationCodes[observation.typeCodeId!] ?? {
                  id: "",
                  name: "N/A",
                  type: "N/A",
                },
                modifiedDate: new Date(observation.observedAt ?? new Date()),
                observedByName: observation.observedByName,
                attachmentUrl: attachments.length > 0 ? attachments[0].url : null,
                isProcessing: attachments[0]?.isProcessing ?? false,
              };
              setObservationDetails((current) =>
                [createdObservation, ...current].sort(
                  (a, b) => b.modifiedDate.getTime() - a.modifiedDate.getTime(),
                ),
              );
            }
            setState("list");
          }}
        />
      ) : (
            <div className="grow">
              <ObservationEditForm
                observationId={selectedObservationId!}
                onCancel={() => setState("list")}
                showFooter={false}
                attachmentColumn={true}
              />
            </div>

        
      )}
      <DeleteDialog
        deleteConfirmationText="Are you sure you want to delete this observation?"
        isOpen={showDeleteFeatureDialog}
        onOpenChange={setShowDeleteFeatureDialog}
        title="Delete Observation"
        onDelete={async () => {
          if (!selectedObservationId) {
            alert("No observation selected for deletion");
            return;
          }
          await ObservationService.delete(selectedObservationId);
          setObservationDetails(
            observationDetails.filter((obs) => obs.id !== selectedObservationId)
          );
        }}
      />
    </>
  );
};

export default MapObservaations;
